import { log } from '@/utils/core/vueLogger.js';

// Same dynamic-attribute contract as deliverylocationVM#getAttributes: per-entity custom fields
// (types 2-5/9/10), filtered by a numeric entityType (11 for delivery locations). Definitions come
// from src/api/core/attribute.js#getAttributes() — the Vue-side, offline-cached source (same
// backend dataset the legacy app keeps in ecosVM.attributes) — so this util has no iframe/ecosVM/
// salesHelper dependency and works from a plain array. Reusable by any v-stepper form
// that needs an "Attributes" step — see DeliveryLocationForm.vue for a reference usage.

export function getAttributeValueTyped(attribute, value) {
	switch (attribute.type) {
		case 1: // string
			return value ? String(value) : '';

		case 2: // int
			return value ? Number(value) : 0;

		case 3: // bool
			return String(value).toLowerCase() === 'true' || String(value) === '1';

		case 4: // OpenList simple
		case 5: // OpenList multi
			if (!value) return '';
			return String(value)
				.split(',')
				.reduce((resolved, raw) => {
					if (raw.trim().length === 0) return resolved;
					const match = (attribute.values ?? []).find((av) => av.id === parseInt(raw, 10));
					if (!match) {
						log('warn', 'DynamicAttributeFields', `⚠️ Unknown attribute value "${raw}" for "${attribute.name}"`);
						return resolved;
					}
					return resolved.length === 0 ? match.value : `${resolved},${match.value}`;
				}, '');

		case 8: // Date
			return value;

		case 9: // money
		case 10: {
			// decimal — same French-comma handling as legacy numHelper.toNumber (public/m.eagle/js/Main.js)
			if (!value) return 0;
			const parsed = Number(String(value).replace(',', '.'));
			return Number.isNaN(parsed) ? 0 : parsed;
		}
	}
}

// Builds stepper form field descriptors for one entityType and seeds their initial value into
// formValues (a ref). `attributes` is the full definitions list from getAttributes().
export function buildDynamicAttributeFields({ attributes, t, entityType, formValues }) {
	if (!Array.isArray(attributes)) return [];

	const defs = attributes.filter((a) => a.entityType === entityType);
	const fields = [];

	defs.forEach((def) => {
		const name = `attr_${def.id}`;
		const field = {
			columns: { sm: 6, xl: 6 },
			label: t(def.name),
			name,
			variant: 'outlined',
		};

		if (def.type === 3) {
			field.type = 'checkbox';
			formValues.value[name] = false;
		} else if (def.type === 4) {
			field.type = 'select';
			field.items = def.values ?? [];
			field['item-title'] = 'value';
			field['item-value'] = 'id';
			field.clearable = true;
			formValues.value[name] = getAttributeValueTyped(def, undefined) ?? null;
		} else if (def.type === 5) {
			field.type = 'select';
			field.items = def.values ?? [];
			field['item-title'] = 'value';
			field['item-value'] = 'id';
			field.multiple = true;
			field.chips = true;
			field.clearable = true;
			formValues.value[name] = [];
		} else if ([2, 9, 10].includes(def.type)) {
			field.type = 'number';
			formValues.value[name] = getAttributeValueTyped(def, undefined) ?? null;
		} else {
			field.type = 'text';
			formValues.value[name] = getAttributeValueTyped(def, undefined) ?? null;
		}

		fields.push(field);
	});

	return fields;
}

// Reverse direction: turns submitted formValues back into the { id, value } pairs the legacy
// saveDlAttributes-style APIs expect, given the field descriptors buildDynamicAttributeFields
// produced.
export function serializeDynamicAttributeFields(fields = [], payload) {
	return fields
		.map((field) => {
			const id = Number(field.name.replace('attr_', ''));
			const raw = payload[field.name];
			let value;
			if (field.type === 'checkbox') value = raw ? 'true' : 'false';
			else if (field.multiple) value = (raw ?? []).join(',');
			else value = raw;
			return { id, value: value !== null && value !== undefined ? String(value) : '' };
		})
		.filter((a) => a.value !== '');
}
